import React, { useState } from 'react'
import { SubscriptionTier } from '../../types'
import PricingTierCard from './PricingTierCard'
import Spinner from '../Spinner'

/**
 * Whole percent a tier's yearly price saves on twelve monthly payments,
 * rounded down so it is never overstated; null without both prices or a saving.
 */
export const yearlySavingPct = (tier: SubscriptionTier): number | null => {
  const yearly = tier.price_yearly_cents
  const twelveMonths = tier.price_monthly_cents * 12
  if (yearly == null || twelveMonths <= 0) return null
  const pct = Math.floor(((twelveMonths - yearly) / twelveMonths) * 100)
  return pct > 0 ? pct : null
}

/** "Save 17%", or "Save up to 20%" when the plans save different amounts. */
export const yearlySavingLabel = (tiers: SubscriptionTier[]): string | null => {
  const savings = tiers.map(yearlySavingPct).filter((pct): pct is number => pct != null)
  if (savings.length === 0) return null
  const best = Math.max(...savings)
  return savings.every((pct) => pct === best) ? `Save ${best}%` : `Save up to ${best}%`
}

interface PricingPlansProps {
  tiers: SubscriptionTier[]
  onSelectTier: (tier: SubscriptionTier, billingCycle: 'monthly' | 'yearly') => void
  currentTierId?: number
  isLoading?: boolean
}

const PricingPlans: React.FC<PricingPlansProps> = ({
  tiers,
  onSelectTier,
  currentTierId,
  isLoading = false,
}) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  const filteredTiers = tiers.filter(
    (tier) =>
      tier.is_active && (billingCycle === 'monthly' || tier.price_yearly_cents != null)
  )

  const handleSelect = (tier: SubscriptionTier) => onSelectTier(tier, billingCycle)

  const activeTiers = tiers.filter((tier) => tier.is_active)
  // No yearly price on any plan: no Monthly/Yearly choice to make.
  const offersYearly = activeTiers.some((tier) => tier.price_yearly_cents != null)
  const savingLabel = yearlySavingLabel(activeTiers)

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Plan</h2>
        <p className="text-gray-600 mb-6">
          Support your favorite creator and get exclusive benefits
        </p>

        {offersYearly && (
        <div className="inline-flex rounded-lg bg-gray-100 p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              billingCycle === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Yearly
            {savingLabel && (
              <span className="ml-2 text-xs text-green-600 font-semibold">{savingLabel}</span>
            )}
          </button>
        </div>
        )}
      </div>

      {filteredTiers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No {billingCycle} plans available at the moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTiers.map((tier) => (
            <PricingTierCard
              key={tier.id}
              tier={tier}
              billingCycle={billingCycle}
              onSelect={handleSelect}
              currentTier={tier.id === currentTierId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default PricingPlans
